'use client';

import { useSession, signOut } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';
// Ссылка витрины, а не next-intl: на русском домене та ведёт на
// /ru/login, middleware уводит редиректом на /login, и после редиректа
// язык теряется — вход открывался на английском. AisLink на этом домене
// отдаёт голый путь, и middleware разворачивает его сам.
//
// На остальных доменах AisLink ведёт себя как обычная ссылка next-intl,
// так что кабинет и DataHunter этого не замечают.
import { AisLink as Link } from '@/components/aisearch/ais-link';
import { useTranslations } from 'next-intl';
import { LayoutDashboard, LogOut } from 'lucide-react';

/**
 * Правый угол шапки: вход или меню вошедшего человека.
 *
 * Раньше кнопка «Войти» висела всегда, даже в кабинете, а выход прятался
 * отдельной кнопкой внутри страницы. Это ломало ожидание: место, где ты
 * вошёл, и место, где выходишь, должны совпадать.
 *
 * Сессия читается на клиенте намеренно. Страницы сайта пререндерятся, и
 * серверное чтение сессии сделало бы их динамическими: пропал бы кэш, а
 * вместе с ним скорость открытия. Поэтому на сервере рисуется нейтральная
 * заготовка, а после гидратации она превращается в нужный вид.
 */
/**
 * @param home Куда ведёт пункт кабинета. На домене продукта главная
 * кабинета закрыта, и ссылка на неё давала бы лишний переход.
 * @param imyaOtSm Имя рядом с кружком — только с ширины sm. В шапке
 * кабинета aisearch.ru рядом стоит колокольчик, и на телефоне длинное имя
 * выталкивало кнопки поверх логотипа; кружок с инициалом остаётся.
 */
export function AccountMenu({
  home = '/dashboard',
  imyaOtSm = false,
}: {
  home?: string;
  imyaOtSm?: boolean;
}) {
  const t = useTranslations('layout');
  const { data: session, status } = useSession();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Клик мимо меню закрывает его: иначе оно висит поверх страницы и
  // перехватывает нажатия, а человек не понимает, почему сайт «завис».
  useEffect(() => {
    if (!open) return;

    const onClickOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  // Пока сессия неизвестна, показываем место под кнопку той же ширины.
  // Без этого шапка дёргается при загрузке, и по ней трудно попасть мышью.
  if (status === 'loading') {
    return <div className="w-[92px] h-9" aria-hidden />;
  }

  if (!session?.user) {
    return (
      <Link
        href="/login"
        // Без предзагрузки (#213): ссылка видна каждому гостю, и Next
        // тянул данные страницы входа ещё до первой отрисовки главной,
        // дважды. Вход открывают редко, а мобильный канал в первые
        // секунды нужнее самой странице.
        prefetch={false}
        className="px-4 py-2 rounded-lg bg-[var(--color-brand-solid,var(--color-brand-500))] hover:bg-[var(--color-brand-400)] text-[var(--color-on-brand)] text-sm font-medium transition-colors"
      >
        {t('signIn')}
      </Link>
    );
  }

  const name = session.user.name ?? session.user.email ?? '';
  // Инициал понятнее пустого кружка: человек видит, что вошёл именно он.
  const initial = name.trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => {
          setOpen(!open);
        }}
        className={`flex items-center gap-2 pl-1.5 ${imyaOtSm ? 'pr-1.5 sm:pr-3' : 'pr-3'} py-1.5 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-text-muted)] transition-colors`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="account-menu"
        // Имя на телефоне скрыто: читалке оно нужно и там.
        aria-label={imyaOtSm ? name : undefined}
      >
        <span className="w-6 h-6 rounded-full bg-[var(--color-brand-solid,var(--color-brand-500))] text-[var(--color-on-brand)] text-xs font-semibold flex items-center justify-center">
          {initial}
        </span>
        <span className={`text-sm max-w-[10rem] truncate${imyaOtSm ? ' hidden sm:inline' : ''}`}>
          {name}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-2xl py-1.5 z-50"
        >
          <div className="px-3 py-2 border-b border-[var(--color-border)] mb-1">
            <div className="text-sm font-medium truncate">{name}</div>
            {session.user.email && session.user.email !== name && (
              <div className="text-xs text-[var(--color-text-muted)] truncate">
                {session.user.email}
              </div>
            )}
          </div>

          <Link
            href={home}
            role="menuitem"
            onClick={() => {
              setOpen(false);
            }}
            className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-[var(--color-bg-card)] transition-colors"
          >
            <LayoutDashboard className="w-4 h-4 text-[var(--color-text-muted)]" />
            {t('dashboard')}
          </Link>

          <button
            role="menuitem"
            onClick={() => {
              void signOut({ redirectTo: '/' });
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-[var(--color-bg-card)] transition-colors text-left border-t border-[var(--color-border)] mt-1 pt-2.5"
          >
            <LogOut className="w-4 h-4 text-[var(--color-text-muted)]" />
            {t('signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
